import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ReassignListsDialog } from 'components/common/pitch-lists/reassign-lists';
import { RestoreListsDialog } from 'components/sections/admin-portal/dialogs/restore-lists';
import { ExportSessionsDialog } from 'components/sections/admin-portal/machines/dialogs/export-sessions';
import { StatcastSearchDialog } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/statcast-search-dialog';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { ResetConnectionsDialog } from 'components/sections/admin-portal/machines/dialogs/reset-connections';
import { RestartFirmwareDialog } from 'components/sections/admin-portal/machines/dialogs/restart-fw';
import { RestartOSDialog } from 'components/sections/admin-portal/machines/dialogs/restart-os';
import { MachineSettingsDialog } from 'components/sections/admin-portal/machines/dialogs/settings';
import { UpgradeFirmwareDialog } from 'components/sections/admin-portal/machines/dialogs/upgrade-firmware';
import { MachineEditor } from 'components/sections/admin-portal/machines/editor';
import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { AdminPortalDialog } from 'enums/admin-portal';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IInstallationDetails } from 'lib_ts/interfaces/i-machine';
import { first } from 'lodash';
import { useCallback, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { useAuthStore } from 'stores/use-auth-store';

export const MachinesTableDialogs = () => {
  const dialog = useMachinesPortalStore((s) => s.dialog);
  const closeDialog = useMachinesPortalStore((s) => s.closeDialog);
  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);

  const { showMachineSettingsDialog, closeMachineSettingsDialog } =
    useMachineSettingsDialogState();

  const statsDialog = usePitchPerformanceStore((s) => s.statsDialog);
  const current = useAuthStore((s) => s.current);

  const {
    machine: connected,
    openDialog: machineCxOpenDialog,
    update: updateMachine,
    createDataCollection,
    extendStandaloneInstallation,
  } = useContext(MachineContext);

  const hasInstall = !!connected.last_installation;

  const isInstalling =
    connected.last_installation && !connected.last_installation.end_date;

  const handleStartOver = useCallback(async () => {
    // close confirmation dialog
    closeDialog();

    const nextInstall: IInstallationDetails = {
      started_by: current.email,
      start_date: new Date().toISOString(),
      ball_type: connected.ball_type,
      ball_type_locked: false,

      btcID: null,
      end_date: null,
      finished_by: null,
      model_id: null,
      rtID: null,
      standalone: null,

      step_ball_type: null,
      step_completion: null,
      step_general_config: null,
      step_hardware_changes: null,
      step_projector_config: null,
      step_repeatability: null,
      step_test_shot: null,
      step_vision: null,

      vccID: null,
      vcdFt: null,
      vcdComplete: null,
    };

    const btcDc = await createDataCollection(
      DataCollectionType.BallTypeCalibration,
      connected.machineID,
      connected.ball_type
    );

    if (btcDc) {
      nextInstall.btcID = btcDc._id;
    }

    const rtDc = await createDataCollection(
      DataCollectionType.RepeatabilityTest,
      connected.machineID,
      connected.ball_type
    );

    if (rtDc) {
      nextInstall.rtID = rtDc._id;
    }

    if (connected.enable_trajekt_vision) {
      const vcc = await VisionCameraCalibrationsService.getInstance().createOne(
        {
          machineID: connected.machineID,
          capturedFrames: [],
        }
      );

      nextInstall.vccID = vcc?._id ?? null;
    }

    await updateMachine({
      last_installation: nextInstall,
    });

    // open the installation dialog
    machineCxOpenDialog(MachineDialogMode.Installation);
  }, [
    connected._id,
    connected.ball_type,
    current.email,
    createDataCollection,
    updateMachine,
  ]);

  const firstMachine = first(dialog?.machines);

  return (
    <>
      {showMachineSettingsDialog && (
        <MachineSettingsDialog onClose={closeMachineSettingsDialog} />
      )}

      {statsDialog && <StatcastSearchDialog key={statsDialog} />}

      {dialog?.type === AdminPortalDialog.ExportSessions && (
        <ExportSessionsDialog
          key={`${AdminPortalDialog.ExportSessions}-${dialog.key}`}
          machineIDs={dialog.machines.map((m) => m.machineID)}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.RestoreList && firstMachine && (
        <RestoreListsDialog
          key={`${AdminPortalDialog.RestoreList}-${dialog.key}`}
          name={firstMachine.machineID ?? '(unknown)'}
          parent_def={PitchListOwner.Machine}
          parent_id={firstMachine._id ?? '(unknown)'}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.Reassign && firstMachine && (
        <ReassignListsDialog
          key={`${AdminPortalDialog.Reassign}-${dialog.key}`}
          identifier="MachinesTableReassignListsDialog"
          onClose={closeDialog}
          refPayload={{
            filter: {
              _parent_id: firstMachine._id,
              _parent_def: PitchListOwner.Machine,
            },
            update: {
              processed: new Date(),
              process_notes: `reassigned from machine ${firstMachine.machineID} by admin ${current.email}`,
              _parent_def: PitchListOwner.Machine,
            },
          }}
        />
      )}

      {dialog?.type === AdminPortalDialog.ResetConnections && firstMachine && (
        <ResetConnectionsDialog
          key={`${AdminPortalDialog.ResetConnections}-${dialog.key}`}
          machineID={firstMachine.machineID}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.RestartFW && (
        <RestartFirmwareDialog
          key={`${AdminPortalDialog.RestartFW}-${dialog.key}`}
          machineIDs={ArrayHelper.unique(
            dialog.machines.map((m) => m.machineID)
          )}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.RestartOS && (
        <RestartOSDialog
          key={`${AdminPortalDialog.RestartOS}-${dialog.key}`}
          machineIDs={ArrayHelper.unique(
            dialog.machines.map((m) => m.machineID)
          )}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.ToggleDeleted && firstMachine && (
        <CommonConfirmationDialog
          key={`${AdminPortalDialog.ToggleDeleted}-${dialog.key}`}
          onClose={closeDialog}
          identifier="ToggleDeleted"
          title={
            firstMachine.deleted ? 'Activate Machine' : 'Deactivate Machine'
          }
          content={
            <ReactMarkdown
              children={[
                `You are about to ${firstMachine.deleted ? 'activate' : 'deactivate'} **${firstMachine.machineID}**.`,
                `Users **${firstMachine.deleted ? 'will' : 'will not'}** be able to use this machine.`,
              ].join('\n\n')}
            />
          }
          action={{
            label: firstMachine.deleted ? 'Activate' : 'Deactivate',
            color: firstMachine.deleted
              ? RADIX.COLOR.ACCENT
              : RADIX.COLOR.DANGER,
            onClick: () =>
              updateMachines([
                {
                  _id: firstMachine._id,
                  deleted: !firstMachine.deleted,
                },
              ]),
          }}
        />
      )}

      {dialog?.type === AdminPortalDialog.FWUpgradeDate && (
        <UpgradeFirmwareDialog
          key={`${AdminPortalDialog.FWUpgradeDate}-${dialog.key}`}
          machines={dialog.machines}
          onClose={closeDialog}
        />
      )}

      {dialog?.type === AdminPortalDialog.ConfirmInstall && hasInstall && (
        // resume or review an existing install
        <CommonConfirmationDialog
          key={`${AdminPortalDialog.ConfirmInstall}-${dialog.key}`}
          identifier="ResumeInstall"
          title={
            isInstalling ? 'Resume Machine Install' : 'Review Machine Install'
          }
          content={
            <ReactMarkdown
              children={
                isInstalling
                  ? 'Do you wish to resume the previous machine install?'
                  : 'Do you wish to review the previous machine install?'
              }
            />
          }
          cancel={{
            label: 'common.start-over',
            color: RADIX.COLOR.ACCENT,
            variant: RADIX.BUTTON.VARIANT.SECONDARY,
            onClick: handleStartOver,
          }}
          action={{
            label: isInstalling ? 'Resume Install' : 'Review Install',
            color: RADIX.COLOR.ACCENT,
            onClick: async () => {
              closeDialog();

              // if the install is a standalone BTC/RT, it will be converted into a full install w/ all other steps
              await extendStandaloneInstallation();

              machineCxOpenDialog(MachineDialogMode.Installation);
            },
          }}
        />
      )}

      {dialog?.type === AdminPortalDialog.EditMachine && (
        // editor only shown as a standalone dialog when creating a new machine
        // otherwise shown in the Configuration tab of MachineSettingsDialog
        <MachineEditor key={dialog.key} onClose={closeDialog} isNew />
      )}
    </>
  );
};
